import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import * as THREE from "three";

// ------------------------------------------------------------
// 3D Background Component (Particle System)
// ------------------------------------------------------------
const ThreeDBackground = () => {
  const mountRef = useRef(null);
  const requestRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const particlesRef = useRef(null);

  useEffect(() => {
    // Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 30;
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0); // transparent background
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Particles
    const particlesGeometry = new THREE.BufferGeometry();
    const particlesCount = 1500;
    const posArray = new Float32Array(particlesCount * 3);
    const colorArray = new Float32Array(particlesCount * 3);

    const indigo = new THREE.Color(0x4f46e5); // indigo-600
    const lightIndigo = new THREE.Color(0xa5b4fc); // indigo-300
    // const white = new THREE.Color(0xffffff);

    for (let i = 0; i < particlesCount * 3; i += 3) {
      // Position in a sphere
      const radius = 20;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      posArray[i] = radius * Math.sin(phi) * Math.cos(theta);
      posArray[i + 1] = radius * Math.sin(phi) * Math.sin(theta);
      posArray[i + 2] = radius * Math.cos(phi);

      // Color
      const mixColor = indigo.clone().lerp(lightIndigo, Math.random());
      colorArray[i] = mixColor.r;
      colorArray[i + 1] = mixColor.g;
      colorArray[i + 2] = mixColor.b;
    }

    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
    particlesGeometry.setAttribute('color', new THREE.BufferAttribute(colorArray, 3));

    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.15,
      vertexColors: true,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
    });

    const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particlesMesh);
    particlesRef.current = particlesMesh;

    // Animation loop
    const animate = () => {
      if (particlesRef.current) {
        particlesRef.current.rotation.y += 0.0002;
        particlesRef.current.rotation.x += 0.0001;
      }
      renderer.render(scene, camera);
      requestRef.current = requestAnimationFrame(animate);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const node = mountRef.current;
      if (node && renderer.domElement) {
        node.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  return <div ref={mountRef} className="fixed inset-0 -z-10" />;
};

// ------------------------------------------------------------
// StatCounter Component
// ------------------------------------------------------------
const StatCounter = ({ label, end, delay = 0 }) => {
  const [num, setNum] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const duration = 900;
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      setNum(Math.floor(t * end));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    const timer = setTimeout(() => {
      raf = requestAnimationFrame(step);
    }, delay * 1000);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [end, delay]);
  return (
    <div className="text-center">
      <div className="text-3xl font-bold text-gray-900">{num}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );
};

// ------------------------------------------------------------
// Main Portfolio Component
// ------------------------------------------------------------
const Portfolio = () => {
  const avatarRefs = useRef([]);
  const [isImageAnimating, setIsImageAnimating] = useState(false);

  useEffect(() => {
    avatarRefs.current = avatarRefs.current.slice(0, 3);
  }, []);

  // Listen for animation trigger event
  useEffect(() => {
    const handleAnimationTrigger = () => {
      setIsImageAnimating(true);
    };
    window.addEventListener('triggerPortfolioAnimation', handleAnimationTrigger);
    return () => {
      window.removeEventListener('triggerPortfolioAnimation', handleAnimationTrigger);
    };
  }, []);

  // Simple navigation function for internal links
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      window.scrollTo({
        top: section.offsetTop - 80,
        behavior: 'smooth'
      });
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="relative bg-gray-50 min-h-screen overflow-hidden">
      {/* 3D Background */}
      <ThreeDBackground />

      {/* Floating image for scroll animation (if triggered) */}
      {isImageAnimating && (
        <motion.div
          className="fixed z-50 w-60 h-60 rounded-lg overflow-hidden border-4 border-white shadow-xl"
          style={{ boxShadow: "0 25px 50px -12px rgba(79, 70, 229, 0.25)" }}
          initial={{
            top: "15%",
            left: "25%",
            scale: 0.75,
            opacity: 1,
          }}
          animate={{
            top: "50%",
            left: "50%",
            x: "-50%",
            y: "-50%",
            scale: 1,
            opacity: 1,
          }}
          transition={{
            duration: 1.5,
            ease: "easeInOut"
          }}
        >
          <img
            src="/pictures/profile.jpg"
            alt="Vivek Yadav"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <div className="w-full h-full hidden items-center justify-center bg-indigo-100 text-indigo-600 text-4xl font-bold">
            VY
          </div>
        </motion.div>
      )}

      {/* Main Content */}
      <div className="relative z-10 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.header
            initial="hidden"
            animate="show"
            variants={headerVariants}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-2">
              Vivek Kumar Yadav
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Developer • ML Enthusiast • Embedded Systems Tinkerer
            </p>
          </motion.header>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column – Profile & Contact */}
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:col-span-1 space-y-6"
            >
              {/* Profile Image */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-72 h-72 rounded-lg overflow-hidden border-4 border-white shadow-md">
                    <img
                      src="/pictures/profile.jpeg"
                      alt="Vivek Yadav"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                    <div className="w-full h-full hidden items-center justify-center bg-indigo-50 text-indigo-600 text-4xl font-bold">
                      VY
                    </div>
                  </div>
                </div>
              </div>

              {/* Intro Text */}
              <div className="text-center lg:text-left">
                <h2 className="text-2xl font-semibold text-indigo-600 mb-2">
                  Hello, I'm Vivek
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Passionate computer aficionado on a journey of discovery through hands-on projects.
                  Intrigued by coding, algorithms, and cutting-edge technologies. Committed to innovative
                  problem-solving and collaborative learning in the dynamic world of Computer Intelligence.
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gradient-to-br from-white to-indigo-50/30 backdrop-blur-sm p-4 rounded-xl border-2 border-indigo-100 text-center shadow-md hover:shadow-lg transition-shadow relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-indigo-200/20 to-transparent rounded-bl-full"></div>
                  <StatCounter label="Projects" end={14} delay={0.2} />
                </div>
                <div className="bg-gradient-to-br from-white to-purple-50/30 backdrop-blur-sm p-4 rounded-xl border-2 border-purple-100 text-center shadow-md hover:shadow-lg transition-shadow relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-purple-200/20 to-transparent rounded-bl-full"></div>
                  <StatCounter label="Commits" end={504} delay={0.5} />
                </div>
                <div className="bg-gradient-to-br from-white to-blue-50/30 backdrop-blur-sm p-4 rounded-xl border-2 border-blue-100 text-center shadow-md hover:shadow-lg transition-shadow relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-blue-200/20 to-transparent rounded-bl-full"></div>
                  <StatCounter label="DSA Sols" end={502} delay={0.9} />
                </div>
              </div>

              {/* Get in touch */}
              <div className="bg-gradient-to-br from-white to-indigo-50/50 backdrop-blur-sm p-6 rounded-xl border-2 border-indigo-100 shadow-md relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-indigo-200/30 to-transparent rounded-bl-full"></div>
                <h3 className="text-lg font-semibold text-indigo-600 mb-4 relative z-10">Get in touch</h3>
                <div className="space-y-3 relative z-10">
                  <a
                    href="mailto:vivekkuyadav21@gmail.com"
                    className="flex items-center p-3 rounded-lg border-2 border-gray-200 hover:border-indigo-300 hover:bg-white/80 transition-all group"
                  >
                    <span className="text-xl mr-3 group-hover:scale-110 transition-transform">✉️</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">Email</div>
                      <div className="text-sm text-gray-500 truncate">vivekkuyadav21@gmail.com</div>
                    </div>
                  </a>
                  <a
                    href="/pictures/VIVEK KUMAR YADAV.pdf"
                    download="VIVEK KUMAR YADAV.pdf"
                    className="flex items-center p-3 rounded-lg border-2 border-gray-200 hover:border-indigo-300 hover:bg-white/80 transition-all group"
                  >
                    <span className="text-xl mr-3 group-hover:scale-110 transition-transform">💼</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">Resume</div>
                      <div className="text-sm text-gray-500 truncate">Download PDF</div>
                    </div>
                  </a>
                  <a
                    href="https://github.com/V1VEK-Y4DAV"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center p-3 rounded-lg border-2 border-gray-200 hover:border-indigo-300 hover:bg-white/80 transition-all group"
                  >
                    <span className="text-xl mr-3 group-hover:scale-110 transition-transform">🐙</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">GitHub</div>
                      <div className="text-sm text-gray-500 truncate">github.com/V1VEK-Y4DAV</div>
                    </div>
                  </a>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => scrollToSection('workbench')}
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  View Projects
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="flex-1 px-4 py-3 border border-indigo-600 text-indigo-600 font-medium rounded-lg hover:bg-indigo-50/70 transition-colors"
                >
                  Contact
                </button>
              </div>
            </motion.section>

            {/* Right Column – About, Tech, Features */}
            <main className="lg:col-span-2 space-y-8">
              {/* About & Values */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <div className="bg-white/90 backdrop-blur-sm p-6 rounded-lg border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-semibold text-indigo-600 mb-3">About Me</h3>
                  <p className="text-gray-700 leading-relaxed">
                    I’m passionate about building systems that are both powerful and intuitive.
                    With hands-on experience in full-stack development, embedded systems, and machine learning,
                    I specialize in creating solutions that seamlessly blend functionality with a refined user experience.
                    My approach combines practical engineering expertise with a strong design sensibility, ensuring that
                    every tool I build is not only technically robust but also enjoyable and efficient to use.
                  </p>
                </div>

                <div className="bg-white/90 backdrop-blur-sm p-6 rounded-lg border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-semibold text-indigo-600 mb-3">What I value</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span>Readable code and clear APIs — Writing clean, well-structured code with intuitive and consistent APIs.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span>Automated tests and reproducible results — Implementing robust testing pipelines to ensure reliability.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span>Simple UX for complex problems — Designing user experiences that make advanced processes feel intuitive.</span>
                    </li>
                  </ul>
                </div>
              </motion.section>

              {/* Education Section */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="rounded-2xl p-8 bg-gradient-to-br from-white to-emerald-50/30 shadow-lg border-2 border-emerald-100 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-200/30 to-transparent rounded-bl-full"></div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 relative z-10">Education</h3>
                <div className="space-y-4 relative z-10">
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-white border-2 border-indigo-100 hover:border-indigo-200 transition-colors shadow-sm">
                    <div className="text-3xl">🎓</div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Bachelor of Technology in Computer Science & Engineering  |  CGPA - 8.52</h4>
                      <p className="text-gray-600 text-sm mt-1">Lovely Professional University | 2023 - 2027</p>
                      <p className="text-gray-500 text-xs mt-2">Relevant Coursework: Data Structures, Algorithms, Machine Learning, Web Development, Database Management</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-white border-2 border-purple-100 hover:border-purple-200 transition-colors shadow-sm">
                    <div className="text-3xl">📚</div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Intermediate (Class XII)  |  Percentage - 84</h4>
                      <p className="text-gray-600 text-sm mt-1">ST. Joseph's School Shaktinagar | 2021 - 2023</p>
                      <p className="text-gray-500 text-xs mt-2">Stream: Science (Physics, Chemistry, Mathematics, Informatics Practices)</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-white border-2 border-blue-100 hover:border-blue-200 transition-colors shadow-sm">
                    <div className="text-3xl">📖</div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Matriculation (Class X)  |  Percentage - 91.2</h4>
                      <p className="text-gray-600 text-sm mt-1">ST. Joseph's School Shaktinagar | 2020 - 2021</p>
                      <p className="text-gray-500 text-xs mt-2">Foundation in Science, Mathematics and Social Science</p>
                    </div>
                  </div>
                </div>
              </motion.section>

              {/* Hobbies */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-white/90 backdrop-blur-sm p-6 rounded-lg border border-gray-200 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-indigo-600 mb-4">Hobbies & Interests</h3>
                <div className="flex flex-wrap gap-2">
                  {["YouTube Learning", "Puzzle Solving", "Open Source", "Competitive Programming"].map((hobby, i) => (
                    <span key={i} className="px-3 py-1.5 bg-gray-100/80 text-gray-800 text-sm rounded-full">
                      {hobby}
                    </span>
                  ))}
                </div>
              </motion.section>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;















// import React, { useEffect, useRef, useState } from "react";
// import { motion } from "framer-motion";


// const StatCounter = ({ label, end, delay = 0 }) => {
//   const [num, setNum] = useState(0);
//   useEffect(() => {
//     let raf;
//     const start = performance.now();
//     const duration = 900;
//     const step = (now) => {
//       const t = Math.min((now - start) / duration, 1);
//       setNum(Math.floor(t * end));
//       if (t < 1) raf = requestAnimationFrame(step);
//     };
//     const timer = setTimeout(() => {
//       raf = requestAnimationFrame(step);
//     }, delay * 1000);
//     return () => {
//       clearTimeout(timer);
//       cancelAnimationFrame(raf);
//     };
//   }, [end, delay]);
//   return (
//     <div className="text-center">
//       <div className="text-3xl font-bold text-white">{num}</div>
//       <div className="text-sm text-gray-400">{label}</div>
//     </div>
//   );
// };

// const FeatureCard = ({ emoji, title, desc, delay = 0 }) => {
//   return (
//     <motion.div
//       initial={{ opacity: 0, y: 20 }}
//       whileInView={{ opacity: 1, y: 0 }}
//       viewport={{ once: true, amount: 0.6 }}
//       transition={{ duration: 0.6, delay }}
//       className="p-6 bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-2xl border border-gray-200 shadow-lg"
//     >
//       <div className="text-2xl mb-3">{emoji}</div>
//       <div className="text-lg text-white font-semibold mb-1">{title}</div>
//       <div className="text-base text-gray-300">{desc}</div>
//     </motion.div>
//   );
// };

// const Portfolio = () => {
//   const avatarRefs = useRef([]);
//   const [isImageAnimating, setIsImageAnimating] = useState(false);
  
//   useEffect(() => {
//     avatarRefs.current = avatarRefs.current.slice(0, 3);
//   }, []);

//   // Listen for animation trigger event
//   useEffect(() => {
//     const handleAnimationTrigger = () => {
//       setIsImageAnimating(true);
//     };
    
//     window.addEventListener('triggerPortfolioAnimation', handleAnimationTrigger);
    
//     return () => {
//       window.removeEventListener('triggerPortfolioAnimation', handleAnimationTrigger);
//     };
//   }, []);

//   // Simple navigation function for internal links
//   const scrollToSection = (sectionId) => {
//     const section = document.getElementById(sectionId);
//     if (section) {
//       window.scrollTo({
//         top: section.offsetTop - 80, // Adjust for fixed navbar
//         behavior: 'smooth'
//       });
//     }
//   };

//   const headerVariants = {
//     hidden: { opacity: 0, y: 18 },
//     show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
//   };

//   return (
//     <div className="relative">
//       {/* Floating image for scroll animation */}
//       {isImageAnimating && (
//         <motion.div
//           className="fixed z-50 w-60 h-60 rounded-2xl overflow-hidden border-4 border-transparent p-[3px] bg-gradient-to-tr from-primary-cyan to-primary-green shadow-xl"
//           style={{
//             transformStyle: "preserve-3d",
//           }}
//           initial={{ 
//             top: "15%",
//             left: "25%",
//             scale: 0.75,
//             opacity: 1,
//           }}
//           animate={{ 
//             top: "50%",
//             left: "50%",
//             x: "-50%",
//             y: "-50%",
//             scale: 1,
//             opacity: 1,
//           }}
//           transition={{ 
//             duration: 1.5, 
//             ease: "easeInOut"
//           }}
//         >
//           <div className="w-full h-full rounded-2xl overflow-hidden bg-gray-800"
//             style={{
//               transform: "translateZ(20px)",
//             }}
//           >
//             <img
//               src="/pictures/profile.jpg"
//               alt="Vivek Yadav"
//               className="w-full h-full object-cover"
//               onError={(e) => {
//                 e.target.style.display = "none";
//                 e.target.nextSibling.style.display = "flex";
//               }}
//             />
//             <div className="w-full h-full flex items-center justify-center text-white text-4xl font-bold">
//               VY
//             </div>
//           </div>
//         </motion.div>
//       )}

//       <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-900 via-gray-800 to-black">
//         <div className="max-w-7xl mx-auto">
//           <motion.header initial="hidden" animate="show" variants={headerVariants} className="text-center mb-4">
//             <h1 className="text-5xl font-extrabold tracking-tight mb-2" style={{ color: '#00aeb5' }}>Vivek Kumar Yadav</h1>
//             <p className="text-lg text-gray-400 max-w-3xl mx-auto">
//               Developer • ML Enthusiast • Embedded Systems tinkerer
//             </p>
//           </motion.header>

//           <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
//             <motion.section
//               initial={{ opacity: 0, y: 30 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.7, delay: 0.1 }}
//               className="lg:col-span-1 flex flex-col items-center lg:items-start space-y-4"
//             >
//               <div className="w-full flex justify-center lg:justify-start -ml-4">
//                 <div className="relative">
//                   {/* 3D Profile Image with animation */}
//                   <motion.div 
//                     className="flex space-y-4 flex-col items-center justify-center"
//                     initial={{ opacity: 1, scale: 1 }}
//                     animate={{ 
//                       opacity: 1, 
//                       scale: 1 
//                     }}
//                     transition={{ duration: 0.8 }}
//                   >
//                     <div className="w-96 h-96 rounded-2xl overflow-hidden border-4 border-transparent p-[3px] bg-gradient-to-tr from-primary-cyan to-primary-green shadow-xl">
//                       <div className="w-full h-full rounded-2xl overflow-hidden bg-gray-800"
//                         style={{
//                           boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
//                         }}
//                       >
//                         <img
//                           src="/pictures/profile.jpg"
//                           alt="Vivek Yadav"
//                           className="w-full h-full object-cover"
//                           style={{
//                             imageRendering: "crisp-edges",
//                             backfaceVisibility: "hidden",
//                           }}
//                           loading="eager"
//                           decoding="async"
//                           fetchPriority="high"
//                           onError={(e) => {
//                             e.target.style.display = "none";
//                             e.target.nextSibling.style.display = "flex";
//                           }}
//                         />
//                         <div className="w-full h-full flex items-center justify-center text-white text-4xl font-bold">
//                           VY
//                         </div>
//                       </div>
//                     </div>
//                   </motion.div>
//                   <div className="absolute -right-10 -bottom-10 hidden lg:flex">
//                     <div className="w-20 h-20 rounded-full bg-primary-cyan flex items-center justify-center text-white shadow-lg">👨‍💻</div>
//                   </div>
//                 </div>
//               </div>

//               <div className="w-full text-center lg:text-left">
//                 <motion.h2 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-2xl font-bold mb-2" style={{ color: '#00aeb5' }}>
//                   Hello, I'm Vivek
//                 </motion.h2>
//                 <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="text-lg text-gray-300">
// "Passionate computer aficionado on a journey of discovery through hands-on projects. Intrigued by coding, algorithms, and cutting-edge technologies. Committed to innovative problem-solving and collaborative learning in the dynamic world of Computer Intelligence"                </motion.p>
//               </div>

//               <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="w-full grid grid-cols-3 gap-3">
//                 <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 p-3 rounded-xl border border-gray-700 text-center">
//                   <StatCounter label="Projects" end={12} delay={0.2} />
//                 </div>
//                 <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 p-3 rounded-xl border border-gray-700 text-center">
//                   <StatCounter label="Commits" end={223} delay={0.5} />
//                 </div>
//                 <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 p-3 rounded-xl border border-gray-700 text-center">
//                   <StatCounter label="Research Paper" end={0} delay={0.9} />
//                 </div>
//               </motion.div>

//               {/* Redesigned "Get in touch" section in sidebar */}
//               <motion.div 
//                 initial={{ opacity: 0 }} 
//                 animate={{ opacity: 1 }} 
//                 transition={{ delay: 1.0 }} 
//                 className="w-full p-6 rounded-2xl bg-gradient-to-br from-gray-800/40 to-gray-900/40 border border-gray-200 shadow-lg"
//               >
//                 <h3 className="text-xl font-semibold mb-4" style={{ color: '#00aeb5' }}>Get in touch</h3>
//                 <div className="space-y-3">
//                   <a 
//                     href="mailto:vivekkuyadav21@gmail.com" 
//                     className="flex items-center p-3 rounded-lg bg-gray-900/60 border border-gray-600 hover:bg-gray-800/70 hover:border-cyan-400/50 transition-all duration-300"
//                   >
//                     <div className="text-xl mr-3">✉️</div>
//                     <div className="flex-1 min-w-0">
//                       <div className="font-medium text-white">Email</div>
//                       <div className="text-sm text-gray-300 truncate">vivekkuyadav21@gmail.com</div>
//                     </div>
//                   </a>
//                   <a 
//                     href="/pictures/VIVEK KUMAR YADAV.pdf" 
//                     download="VIVEK KUMAR YADAV.pdf"
//                     className="flex items-center p-3 rounded-lg bg-gray-900/60 border border-gray-600 hover:bg-gray-800/70 hover:border-cyan-400/50 transition-all duration-300"
//                   >
//                     <div className="text-xl mr-3">💼</div>
//                     <div className="flex-1 min-w-0">
//                       <div className="font-medium text-white">Resume</div>
//                       <div className="text-sm text-gray-300 truncate">Download PDF</div>
//                     </div>
//                   </a>
//                   <a 
//                     href="https://github.com/V1VEK-Y4DAV" 
//                     target="_blank" 
//                     rel="noreferrer"
//                     className="flex items-center p-3 rounded-lg bg-gray-900/60 border border-gray-600 hover:bg-gray-800/70 hover:border-cyan-400/50 transition-all duration-300"
//                   >
//                     <div className="text-xl mr-3">🐙</div>
//                     <div className="flex-1 min-w-0">
//                       <div className="font-medium text-white">GitHub</div>
//                       <div className="text-sm text-gray-300 truncate">github.com/V1VEK-Y4DAV</div>
//                     </div>
//                   </a>
//                 </div>
//               </motion.div>

//               <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="w-full">
//                 <div className="flex space-x-3">
//                   <button onClick={() => scrollToSection('workbench')} className="flex-1 px-4 py-3 bg-primary-cyan rounded-lg font-semibold text-black hover:brightness-105 transition">View Projects</button>
//                   <button onClick={() => scrollToSection('contact')} className="px-4 py-3 border border-primary-cyan text-primary-cyan rounded-lg hover:bg-primary-cyan hover:text-black transition">Contact</button>
//                 </div>
//               </motion.div>
//             </motion.section>

//             <main className="lg:col-span-2 space-y-8">
//               <motion.section initial={{ opacity: 10, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="p-6 rounded-2xl bg-gradient-to-br from-gray-800/40 to-gray-900/40 border border-gray-200 shadow-lg">
//                     <h3 className="text-xl font-semibold mb-3" style={{ color: '#00aeb5' }}>About Me</h3>
//                     <p className="text-base leading-relaxed text-gray-300">
//                     I’m passionate about building systems that are both powerful and intuitive. With hands-on experience in full-stack development, embedded systems, and machine learning, I specialize in creating solutions that seamlessly blend functionality with a refined user experience. My approach combines practical engineering expertise with a strong design sensibility, ensuring that every tool I build is not only technically robust but also enjoyable and efficient to use.
//                     </p>
//                   </div>

//                   <div className="p-6 rounded-2xl bg-gradient-to-br from-gray-800/40 to-gray-900/40 border border-gray-00 shadow-lg">
//                     <h3 className="text-xl font-semibold mb-3" style={{ color: '#00aeb5' }}>What I value</h3>
//                     <ul className="text-base space-y-2 list-disc list-inside text-gray-300">
//                     <li>
//   Readable code and clear APIs — Writing clean, well-structured code with intuitive and consistent APIs, making it easy for developers to understand, maintain, and extend.
// </li>
// <li>
//   Automated tests and reproducible results — Implementing robust automated testing pipelines to ensure reliability, prevent regressions, and guarantee that results can be consistently reproduced across different environments.
// </li>
// <li>
//   Simple UX for complex problems — Designing user experiences that make even the most advanced or technical processes feel intuitive, accessible, and efficient for end-users.
// </li>

//                     </ul>
//                   </div>
//                 </div>
//               </motion.section>

//               {/* Tech Stack Names in Box with Responsive Layout */}
//               <motion.section 
//                 initial={{ opacity: 0, y: 20 }} 
//                 whileInView={{ opacity: 1, y: 0 }} 
//                 viewport={{ once: true }} 
//                 transition={{ duration: 0.6 }}
//                 className="p-6 rounded-2xl bg-gradient-to-br from-gray-800/40 to-gray-900/40 border border-gray-200 shadow-lg"
//               >
//                 <h3 className="text-xl font-semibold mb-6" style={{ color: '#00aeb5' }}>Tech Stack</h3>
//                 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
//                   {["React", "Tailwind CSS", "Framer Motion", "JavaScript", "Python", "IoT", "Machine Learning", "TensorFlow", "scikit-learn", "Pandas", "NumPy", "Matplotlib", "Data Analysis", "GeoPandas", "Node.js", "PHP", "MongoDB", "MySQL", "XAMPP", "Database Design", "SQL", "C++", "Data Structures", "Algorithms"].map((tech, index) => (
//                     <div key={index} className="text-[#00aeb5] text-sm bg-gray-700/30 px-3 py-2 rounded-lg text-center">
//                       {tech}
//                     </div>
//                   ))}
//                 </div>
//               </motion.section>

//               <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
//                 <FeatureCard emoji="⚡" title="Fast Prototyping" desc="Turn ideas into prototypes quickly and iteratively." delay={0.1} />
//                 <FeatureCard emoji="🔒" title="Security Mindset" desc="Think about authentication and data safety from the start." delay={0.2} />
//                 <FeatureCard emoji="📈" title="Data-driven" desc="Use metrics and logs to improve product and performance." delay={0.3} />
//               </motion.section>

//               <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="p-6 rounded-2xl bg-gradient-to-br from-gray-800/40 to-gray-900/40 border border-gray-200 shadow-lg">
//                 <h3 className="text-xl font-semibold mb-6" style={{ color: '#00aeb5' }}>Hobbies & Interests</h3>
//                 <div className="flex flex-wrap gap-3">
//                   <span className="px-3 py-1 rounded-full bg-gray-800/40 border border-gray-700 text-gray-300">YouTube Learning</span>
//                   <span className="px-3 py-1 rounded-full bg-gray-800/40 border border-gray-700 text-gray-300">Puzzle Solving</span>
//                   <span className="px-3 py-1 rounded-full bg-gray-800/40 border border-gray-700 text-gray-300">Open Source</span>
//                   <span className="px-3 py-1 rounded-full bg-gray-800/40 border border-gray-700 text-gray-300">Cycling</span>
//                   <span className="px-3 py-1 rounded-full bg-gray-800/40 border border-gray-700 text-gray-300">Cooking</span>
//                 </div>
//               </motion.section>

//             </main>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Portfolio;









